import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { GraduationCap, Loader2, Lock, Trash2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../EmptyState';

/**
 * BD-14 — the Coach tab: this person's own call scores, a trend per dimension, their best moments,
 * demo practice with attempts compared, scores shared with them, and the firm's anonymous trend.
 *
 * Scores are PRIVATE: the page only ever asks for the signed-in person's own (the API answers 404 for
 * anyone else's, including to an admin). The firm view carries no names, calls or quotes.
 */

interface Summary {
  id: number;
  kind: 'call' | 'practice';
  status: 'running' | 'ready' | 'failed';
  label: string | null;
  meeting_id: number | null;
  title: string | null;
  call_at: string | null;
  overall_score: number | null;
  scored_at: string | null;
  error: string | null;
  focus: string | null;
  owner_name?: string | null;
}
interface Dim { key: string; name: string; framework: string; score: number | null; feedback: string; quote: string | null; cited?: boolean }
interface Detail extends Summary {
  shared_with_me: boolean;
  owner_name?: string | null;
  dimensions: Dim[];
  strengths: { text: string; quote: string }[];
  improvements: { text: string; quote: string | null }[];
  best_moments: { quote: string; why: string; dimension: string | null }[];
  coaching_focus: { text: string; quote: string | null } | null;
  alignment: { screen?: string } | null;
  shared_with?: { user_id: number; name: string }[];
}
interface Point { id: number; date: string; score: number }
interface Trend { key: string; name: string; points: Point[] }
interface Attempt { id: number; attempt: number; status: string; created_at: string; overall_score: number | null; delta_overall: number | null; error: string | null }
interface PracticeSession { label: string; attempts: Attempt[]; best: number | null }
interface Overview {
  opted_in: boolean;
  sharing_trend: boolean;
  scores: Summary[];
  trends: { overall: Point[]; dimensions: Trend[] };
  best_moments: { quote: string; why: string; score_id: number; title: string | null; date: string | null }[];
  practice: PracticeSession[];
  dimensions: { key: string; name: string; framework: string; weight: number; proposed: boolean }[];
}
interface Firm {
  available: boolean;
  reason?: string;
  min_cohort?: number;
  months?: { month: string; calls: number; people: number; overall: number | null; dimensions: Record<string, number> }[];
  dimension_names?: Record<string, string>;
}

const when = (iso: string | null | undefined): string => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/** A small line of the scores over time. No chart library: the shape is what matters here. */
export const Sparkline: React.FC<{ points: Point[]; testid?: string }> = ({ points, testid }) => {
  if (points.length < 2) {
    return <span className="text-xs text-gray-400" data-testid={testid}>{points.length === 1 ? `${points[0].score} (first score)` : 'no scores yet'}</span>;
  }
  const w = 120;
  const h = 32;
  const xs = points.map((_, i) => (i / (points.length - 1)) * (w - 4) + 2);
  const ys = points.map((p) => h - 2 - (Math.max(0, Math.min(100, p.score)) / 100) * (h - 4));
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const first = points[0].score;
  const last = points[points.length - 1].score;
  return (
    <span className="inline-flex items-center gap-2" data-testid={testid}>
      <svg width={w} height={h} role="img" aria-label={`from ${first} to ${last}`}>
        <path d={d} fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600" />
      </svg>
      <span className={`text-xs font-medium ${last >= first ? 'text-green-700' : 'text-amber-700'}`}>{last >= first ? '+' : ''}{last - first}</span>
    </span>
  );
};

const ScoreBar: React.FC<{ score: number | null }> = ({ score }) => (
  score === null
    ? <span className="text-xs text-gray-400">not applicable</span>
    : (
      <span className="inline-flex items-center gap-2">
        <span className="w-24 h-2 rounded bg-gray-100 overflow-hidden"><span className="block h-2 bg-blue-600" style={{ width: `${score}%` }} /></span>
        <span className="text-sm font-medium text-gray-900 w-8">{score}</span>
      </span>
    )
);

const Quote: React.FC<{ text: string }> = ({ text }) => (
  <blockquote className="mt-1 border-l-2 border-gray-300 pl-3 text-xs italic text-gray-600" data-testid="coach-quote">“{text}”</blockquote>
);

// ── one score ────────────────────────────────────────────────────────────────────

const ScoreDetail: React.FC<{ id: number; onClose: () => void; onChanged: () => void }> = ({ id, onClose, onChanged }) => {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [missing, setMissing] = useState(false);
  const [reviewers, setReviewers] = useState<{ id: number; name: string }[]>([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/bd/coach/scores/${id}`);
      setDetail(res.data.score);
      setMissing(false);
    } catch (err) {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  // Only the owner of a score can share it, so only then is the list of reviewers needed.
  const detailId = detail ? detail.id : null;
  const isOwner = detail ? !detail.shared_with_me : false;
  useEffect(() => {
    if (detailId !== null && isOwner) {
      api.get('/api/bd/coach/reviewers').then((r) => setReviewers(r.data.reviewers || [])).catch(() => setReviewers([]));
    }
  }, [detailId, isOwner]);

  const share = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await api.post(`/api/bd/coach/scores/${id}/shares`, { reviewer_user_id: Number(pick) });
      setPick('');
      await load();
      toast.success('Shared. They can read this one score until you stop sharing.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not share the score.');
    } finally {
      setBusy(false);
    }
  };
  const unshare = async (reviewerId: number) => {
    try {
      await api.delete(`/api/bd/coach/scores/${id}/shares/${reviewerId}`);
      await load();
    } catch (err) {
      toast.error('Could not stop sharing.');
    }
  };
  const remove = async () => {
    if (!window.confirm('Delete this score? This also stops any sharing of it.')) return;
    try {
      await api.delete(`/api/bd/coach/scores/${id}`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error('Could not delete the score.');
    }
  };
  const rescore = async () => {
    if (!detail || !detail.meeting_id) return;
    setBusy(true);
    try {
      await api.post(`/api/bd/coach/meetings/${detail.meeting_id}/score`, { force: true });
      await load();
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not score the call.');
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div className="border rounded-lg p-4 bg-white" data-testid="coach-detail-missing">
        <p className="text-sm text-gray-700">That score is not available to you.</p>
        <button type="button" onClick={onClose} className="mt-2 text-sm text-blue-600 hover:underline">Back to the Coach tab</button>
      </div>
    );
  }
  if (!detail) return <div className="p-6 text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  const owner = !detail.shared_with_me;
  return (
    <div className="border rounded-lg bg-white" data-testid="coach-detail">
      <div className="flex items-start justify-between p-4 border-b">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{detail.kind === 'practice' ? (detail.label || 'Demo practice') : (detail.title || 'Call')}</h3>
          <p className="text-xs text-gray-500">
            {detail.kind === 'practice' ? 'Practice attempt' : 'Call'} · {when(detail.call_at || detail.scored_at)}
            {detail.shared_with_me && ` · shared with you by ${detail.owner_name || 'a colleague'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {detail.overall_score !== null && <span className="text-2xl font-bold text-gray-900" data-testid="coach-detail-overall">{detail.overall_score}</span>}
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {detail.status === 'running' && <p className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Still scoring…</p>}
        {detail.status === 'failed' && <p className="text-sm text-red-600" data-testid="coach-detail-failed">{detail.error || 'This could not be scored.'}</p>}

        {detail.coaching_focus && (
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-3" data-testid="coach-focus">
            <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">One thing to try next time</p>
            <p className="text-sm text-gray-900 mt-1">{detail.coaching_focus.text}</p>
            {detail.coaching_focus.quote && <Quote text={detail.coaching_focus.quote} />}
            {owner && detail.kind === 'call' && <p className="text-xs text-blue-700 mt-2">This also goes into your next prep brief.</p>}
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">{detail.kind === 'practice' ? 'Each step of the demo' : 'Dimensions'}</h4>
          <ul className="space-y-3">
            {detail.dimensions.map((d) => (
              <li key={d.key} data-testid={`coach-dim-${d.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-900">{d.name}<span className="ml-2 text-xs font-normal text-gray-400">{d.framework}</span></span>
                  <ScoreBar score={d.score} />
                </div>
                <p className="text-sm text-gray-700 mt-1">{d.feedback}</p>
                {d.quote ? <Quote text={d.quote} /> : d.score !== null && <p className="text-xs text-gray-400 mt-1">No matching quote could be confirmed in the call.</p>}
              </li>
            ))}
          </ul>
        </div>

        {detail.strengths.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">What went well</h4>
            <ul className="space-y-2">{detail.strengths.map((s, i) => <li key={i} className="text-sm text-gray-700">{s.text}<Quote text={s.quote} /></li>)}</ul>
          </div>
        )}
        {detail.improvements.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">What to improve</h4>
            <ul className="space-y-2">{detail.improvements.map((s, i) => <li key={i} className="text-sm text-gray-700">{s.text}{s.quote && <Quote text={s.quote} />}</li>)}</ul>
          </div>
        )}
        {detail.alignment && detail.alignment.screen && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Screen alignment</h4>
            <p className="text-sm text-gray-700">{detail.alignment.screen}</p>
          </div>
        )}

        {owner && detail.status === 'ready' && (
          <div className="border-t pt-4" data-testid="coach-share">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Share with a reviewer</h4>
            <p className="text-xs text-gray-500 mb-2">Only the person you name can read this one score, until you stop sharing.</p>
            {(detail.shared_with || []).length > 0 && (
              <ul className="mb-2 space-y-1">
                {(detail.shared_with || []).map((r) => (
                  <li key={r.user_id} className="text-sm text-gray-700 flex items-center gap-2">{r.name}
                    <button type="button" onClick={() => unshare(r.user_id)} className="text-xs text-red-600 hover:underline">Stop sharing</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Reviewer" className="text-sm border rounded px-2 py-1.5">
                <option value="">Choose a reviewer…</option>
                {reviewers.filter((r) => !(detail.shared_with || []).some((s) => s.user_id === r.id)).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <button type="button" onClick={share} disabled={!pick || busy} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50">Share</button>
            </div>
          </div>
        )}
        {owner && (
          <div className="flex items-center gap-4 text-sm">
            {detail.kind === 'call' && detail.meeting_id && (
              <>
                <Link to={`/app/bd-meetings/${detail.meeting_id}`} className="text-blue-600 hover:underline">Open the meeting</Link>
                <button type="button" onClick={rescore} disabled={busy} className="text-blue-600 hover:underline disabled:opacity-50">Score again</button>
              </>
            )}
            <button type="button" onClick={remove} className="inline-flex items-center gap-1 text-red-600 hover:underline"><Trash2 className="w-3.5 h-3.5" />Delete</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── practice ─────────────────────────────────────────────────────────────────────

const PracticePanel: React.FC<{ sessions: PracticeSession[]; onOpen: (id: number) => void; onChanged: () => void; optedIn: boolean }> = ({ sessions, onOpen, onChanged, optedIn }) => {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose a recording of the demo.'); return; }
    const form = new FormData();
    form.append('video', file);
    if (label.trim()) form.append('label', label.trim());
    setBusy(true);
    try {
      await api.post('/api/bd/coach/practice', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded. Scoring can take a few minutes; it will appear below.');
      setLabel('');
      if (fileRef.current) fileRef.current.value = '';
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not upload the recording.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="coach-practice">
      <h3 className="text-sm font-semibold text-gray-900">Demo practice</h3>
      <p className="text-xs text-gray-500">Upload a rehearsal (or a live demo recording). It is scored on the FAVORITE demo steps, with what was on screen at the time. Attempts at the same demo are compared.</p>
      {optedIn ? (
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Demo name (e.g. Jarvis intro)" aria-label="Demo name" className="text-sm border rounded px-2 py-1.5 w-56" maxLength={120} />
          <input ref={fileRef} type="file" accept="video/*,audio/*" aria-label="Recording" className="text-sm" />
          <button type="submit" disabled={busy} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50"><Upload className="w-3.5 h-3.5" />Score it</button>
        </form>
      ) : <p className="text-xs text-amber-700">Turn on Coaching in Settings → BD Assistant to score a practice recording.</p>}
      {sessions.length === 0 ? <p className="text-sm text-gray-400">No practice attempts yet.</p> : (
        <ul className="space-y-3">
          {sessions.map((s) => (
            <li key={s.label} className="border rounded-lg p-3 bg-white" data-testid="coach-practice-session">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{s.label}</span>
                {s.best !== null && <span className="text-xs text-gray-500">best {s.best}</span>}
              </div>
              <ul className="mt-2 space-y-1">
                {s.attempts.map((a) => (
                  <li key={a.id} className="text-sm flex items-center gap-3">
                    <span className="text-gray-500 w-20">Attempt {a.attempt}</span>
                    {a.status === 'running' && <span className="text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />scoring…</span>}
                    {a.status === 'failed' && <span className="text-red-600" title={a.error || ''}>could not be scored</span>}
                    {a.status === 'ready' && (
                      <>
                        <button type="button" onClick={() => onOpen(a.id)} className="text-blue-600 hover:underline font-medium">{a.overall_score}</button>
                        {a.delta_overall !== null && <span className={`text-xs ${a.delta_overall >= 0 ? 'text-green-700' : 'text-amber-700'}`} data-testid="coach-attempt-delta">{a.delta_overall >= 0 ? '+' : ''}{a.delta_overall} vs the attempt before</span>}
                      </>
                    )}
                    <span className="text-xs text-gray-400">{when(a.created_at)}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ── the firm's scoring settings (admin) ─────────────────────────────────────────

const AdminSettings: React.FC<{ dimensions: Overview['dimensions'] }> = ({ dimensions }) => {
  const [cfg, setCfg] = useState<{ dimensions: Overview['dimensions']; consulting_dimensions: boolean; min_cohort: number } | null>(null);
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [consulting, setConsulting] = useState(true);
  const [cohort, setCohort] = useState('3');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/bd/coach/config').then((res) => {
      const c = res.data.config;
      setCfg(c);
      setWeights(Object.fromEntries(c.dimensions.map((d: any) => [d.key, String(d.weight)])));
      setConsulting(c.consulting_dimensions);
      setCohort(String(c.min_cohort));
    }).catch(() => setCfg(null));
  }, [dimensions.length]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/api/bd/coach/config', {
        weights: Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, Number(v)])),
        consulting_dimensions: consulting,
        min_cohort: Number(cohort),
      });
      setCfg(res.data.config);
      toast.success('Saved. Calls are re-scored the next time they are opened or logged.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };
  if (!cfg) return null;
  return (
    <section className="space-y-2 border-t pt-4" data-testid="coach-admin">
      <h3 className="text-sm font-semibold text-gray-900">Scoring settings (admin)</h3>
      <p className="text-xs text-gray-500">Weights decide how much each dimension counts toward the overall score (0 leaves it out).</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {cfg.dimensions.map((d) => (
          <label key={d.key} className="flex items-center justify-between gap-2 text-sm text-gray-700">
            <span>{d.name}{d.proposed && <span className="ml-1 text-xs text-amber-700">(proposed)</span>}</span>
            <input type="number" min={0} max={10} step={0.5} value={weights[d.key] ?? ''} onChange={(e) => setWeights({ ...weights, [d.key]: e.target.value })} aria-label={`${d.name} weight`} className="w-20 border rounded px-2 py-1 text-sm" />
          </label>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={consulting} onChange={(e) => setConsulting(e.target.checked)} />Score the two proposed consulting dimensions (Insight delivered, Next step secured)</label>
      <label className="flex items-center gap-2 text-sm text-gray-700">Smallest group the firm view may show
        <input type="number" min={3} max={50} value={cohort} onChange={(e) => setCohort(e.target.value)} aria-label="Smallest group" className="w-16 border rounded px-2 py-1 text-sm" />
      </label>
      <button type="button" onClick={save} disabled={saving} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50">Save</button>
    </section>
  );
};

// ── the tab ──────────────────────────────────────────────────────────────────────

const CoachTab: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [data, setData] = useState<Overview | null>(null);
  const [shared, setShared] = useState<Summary[]>([]);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [failed, setFailed] = useState(false);
  const open = Number(new URLSearchParams(location.search).get('score')) || null;

  const load = useCallback(async () => {
    try {
      const [o, s, f] = await Promise.all([
        api.get('/api/bd/coach/overview'),
        api.get('/api/bd/coach/shared').catch(() => ({ data: { scores: [] } })),
        api.get('/api/bd/coach/firm').catch(() => ({ data: null })),
      ]);
      setData(o.data);
      setShared(s.data.scores || []);
      setFirm(f.data);
      setFailed(false);
    } catch (err) {
      setFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A practice recording scores in the background: look again while one is running.
  const running = !!data && data.practice.some((p) => p.attempts.some((a) => a.status === 'running'));
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  const openScore = (id: number | null) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', 'coach');
    if (id) params.set('score', String(id)); else params.delete('score');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  if (failed) return <EmptyState icon={GraduationCap} title="Coach could not load" description="Try again in a moment." />;
  if (!data) return <div className="p-6 text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  const latestFocus = data.scores.find((s) => s.status === 'ready' && s.focus);

  return (
    <div className="space-y-8" data-testid="coach-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Coach</h2>
          <p className="text-sm text-gray-500 flex items-center gap-1"><Lock className="w-3.5 h-3.5" />Only you can see your scores. You can share one with a named reviewer.</p>
        </div>
        <Link to="/app/sales-call-coach" className="text-xs text-gray-500 hover:underline whitespace-nowrap">Score an upload instead</Link>
      </div>

      {!data.opted_in && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="coach-off">
          Coaching is off for you, so new calls are not scored. Turn it on in <Link to="/app/settings?tab=bd-assistant" className="underline">Settings → BD Assistant</Link>; every call you log with a transcript is then scored privately.
        </div>
      )}

      {open && <ScoreDetail id={open} onClose={() => openScore(null)} onChanged={load} />}

      {latestFocus && !open && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3" data-testid="coach-latest-focus">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Your focus for the next call</p>
          <p className="text-sm text-gray-900 mt-1">{latestFocus.focus}</p>
        </div>
      )}

      <section data-testid="coach-history">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Your scored calls</h3>
        {data.scores.length === 0 ? (
          <p className="text-sm text-gray-400">{data.opted_in ? 'No calls scored yet. A call with a transcript is scored as soon as it is logged.' : 'No scored calls.'}</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Call</th><th>Date</th><th>Score</th></tr></thead>
            <tbody>
              {data.scores.map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="py-2"><button type="button" onClick={() => openScore(s.id)} className="text-blue-600 hover:underline text-left">{s.title || 'Call'}</button></td>
                  <td className="text-gray-500">{when(s.call_at || s.scored_at)}</td>
                  <td>{s.status === 'ready' ? <span className="font-medium">{s.overall_score ?? '–'}</span> : s.status === 'running' ? <span className="text-gray-400">scoring…</span> : <span className="text-red-600">failed</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {data.trends.dimensions.length > 0 && (
        <section data-testid="coach-trends">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Trend per dimension</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <li className="flex items-center justify-between border rounded-lg p-2"><span className="text-sm font-medium">Overall</span><Sparkline points={data.trends.overall} testid="coach-trend-overall" /></li>
            {data.trends.dimensions.map((d) => (
              <li key={d.key} className="flex items-center justify-between border rounded-lg p-2"><span className="text-sm text-gray-700">{d.name}</span><Sparkline points={d.points} testid={`coach-trend-${d.key}`} /></li>
            ))}
          </ul>
        </section>
      )}

      {data.best_moments.length > 0 && (
        <section data-testid="coach-moments">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Your best moments</h3>
          <ul className="space-y-3">
            {data.best_moments.map((m, i) => (
              <li key={i}>
                <Quote text={m.quote} />
                <p className="text-xs text-gray-500 mt-1">{m.why}{m.title && ` · ${m.title}`}{m.date && ` · ${when(m.date)}`}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PracticePanel sessions={data.practice} onOpen={openScore} onChanged={load} optedIn={data.opted_in} />

      {shared.length > 0 && (
        <section data-testid="coach-shared">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Shared with you</h3>
          <ul className="space-y-1">
            {shared.map((s) => (
              <li key={s.id} className="text-sm"><button type="button" onClick={() => openScore(s.id)} className="text-blue-600 hover:underline">{s.title || s.label || 'Call'}</button><span className="text-gray-500"> · from {s.owner_name || 'a colleague'} · {s.overall_score ?? '–'}</span></li>
            ))}
          </ul>
        </section>
      )}

      <section data-testid="coach-firm">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Firm trend</h3>
        {firm && firm.available ? (
          (firm.months || []).length === 0
            ? <p className="text-sm text-gray-400">Not enough people contributed in any one month to show a trend yet.</p>
            : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Month</th><th>Overall</th>{Object.keys(firm.dimension_names || {}).filter((k) => (firm.months || []).some((m) => k in m.dimensions)).map((k) => <th key={k}>{(firm.dimension_names || {})[k]}</th>)}<th>People</th></tr></thead>
                <tbody>
                  {(firm.months || []).map((m) => (
                    <tr key={m.month} className="border-t">
                      <td className="py-1">{m.month}</td><td>{m.overall ?? '–'}</td>
                      {Object.keys(firm.dimension_names || {}).filter((k) => (firm.months || []).some((x) => k in x.dimensions)).map((k) => <td key={k}>{m.dimensions[k] ?? '–'}</td>)}
                      <td>{m.people}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        ) : (
          <p className="text-sm text-gray-400" data-testid="coach-firm-unavailable">The firm trend is anonymous and only shows once at least {firm?.min_cohort || 3} people have chosen to share theirs. {data.sharing_trend ? 'You are sharing yours.' : 'You can choose to share yours in Settings → BD Assistant.'}</p>
        )}
      </section>

      {isAdmin && <AdminSettings dimensions={data.dimensions} />}
    </div>
  );
};

export default CoachTab;
